import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { ITableColumn } from 'src/app/models/tableColumns';
import { AccessGroupsTableColumns } from 'src/app/models/tableColumns/accessGroupsTableColumns.model';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { ITableRowViewModel } from 'src/app/models/viewModels/tableRowViewModel';
import { AccessGroupApiResponse, AccessGroupRowViewModel } from 'src/app/models/api/accountManagement.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { Dialog } from '@angular/cdk/dialog';
import { MatDialog } from '@angular/material/dialog';
import {
  AccessGroupDialogComponent,
  AccessGroupDialogData,
  AccessGroupDialogResult,
} from 'src/app/components/access-group-dialog/access-group-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmData,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { MenuAction } from 'src/app/models/menuAction.model';
import { RowClicked } from 'src/app/components/table-row/table-row.component';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { getRoleDisplayName } from 'src/app/models/constants/accessGroupRoles.constants';
import { AccessGroupsSortingService } from 'src/app/services/actions/access-groups/access-groups-sorting.service';

export type AccessGroupsVM = ITableRowViewModel & AccessGroupRowViewModel;

export interface RowActionClicked extends RowClicked {
  action: MenuAction;
}

@Component({
  selector: 'app-access-groups',
  templateUrl: './access-groups.component.html',
  styleUrls: ['./access-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessGroupsComponent implements OnInit {
  public columns = signal<ITableColumn[]>(AccessGroupsTableColumns);
  public pageName = TableColumnsNames.AccessGroups;

  public isLoading = signal<boolean>(false);
  public showNoData = signal<boolean>(false);

  public accessGroups$ = new BehaviorSubject<AccessGroupsVM[]>([]);

  public pageInfo = signal<ITablePageInfo>({ number: 1, maxSize: 50, currSize: 0, totalItems: 0, totalPages: 1 });
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
  public readonly defaultPageSize = signal<number>(50);

  // simple filters model (only search) for the filters component
  public pageFilters = {
    search: '',
    updateSearch: (value?: string) => {
      this.pageFilters.search = value ?? '';
      this._applySearch();
    },
  };

  private _allRows: AccessGroupsVM[] = [];
  private _rolesMap: Record<string, string> = {};

  private readonly _rowActions: MenuAction[] = [
    {
      name: 'edit',
      displayNameLabel: 'userManagementPage.accessGroupsPage.actions.edit',
      disabled: false,
      shouldOpenDialog: true,
      iconPath: 'assets/images/common/edit.svg',
    },
    {
      name: 'delete',
      displayNameLabel: 'userManagementPage.accessGroupsPage.actions.delete',
      disabled: false,
      shouldOpenDialog: true,
      iconPath: 'assets/images/common/delete.svg',
    },
  ];

  private _currentSort: SortColumn | undefined;

  constructor(
    private readonly _dialog: Dialog,
    private readonly _matDialog: MatDialog,
    private readonly _accountHttpService: AccountManagementHttpService,
    private readonly _logger: LoggerService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _storeService: StoreService,
    private readonly _notificationsService: NotificationsService,
    private readonly _destroyRef: DestroyRef,
    private readonly _sortingService: AccessGroupsSortingService
  ) {}

  ngOnInit(): void {
    this._setDefaultSort();
    this._subscribeToSortChanges();
    this._loadData();
  }

  private _loadData(): void {
    this.isLoading.set(true);
    this.accessGroups$.next([]);

    forkJoin({
      roles: this._accountHttpService.getRoles().pipe(
        catchError((error) => {
          this._logger.error('Error loading roles:', error);
          return of([]);
        })
      ),
      accessGroups: this._accountHttpService.getAccessGroups().pipe(
        catchError((error) => {
          this._handleError(error);
          return of({ _embedded: { items: [] }, page: undefined });
        })
      ),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ({ roles, accessGroups }) => {
          this._rolesMap = roles.reduce(
            (acc, role) => {
              acc[role.id] = role.name;
              return acc;
            },
            {} as Record<string, string>
          );

          const items = accessGroups._embedded?.items || [];
          const viewModels = items.map((item) => this._transformToViewModel(item));

          this._allRows = viewModels;
          this._applySortAndFilters();

          if (accessGroups.page) {
            const tablePageInfo = jsonHalPageInfoToTablePageInfo(accessGroups.page, this.defaultPageSize());
            this.pageInfo.set(tablePageInfo);
          } else {
            this.pageInfo.update((p) => ({
              ...p,
              totalItems: viewModels.length,
              currSize: viewModels.length,
              totalPages: 1,
            }));
          }

          this.showNoData.set(viewModels.length === 0);
        },
      });
  }

  private _setDefaultSort(): void {
    const lastChangedCol = (this.columns() || []).find(
      (c) => c.sortKey === 'last_changed' || c.propertyKey === 'lastChanged'
    );
    if (lastChangedCol) {
      this._currentSort = { column: lastChangedCol, isDesc: true };
      this.sortedColumn$.next(this._currentSort);
    }
  }

  private _subscribeToSortChanges(): void {
    this.sortedColumn$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((sortColumn) => {
      if (sortColumn) {
        this._currentSort = sortColumn;
        if (this._allRows.length > 0) {
          this._applySortAndFilters();
        }
      }
    });
  }

  private _applySortAndFilters(): void {
    let data = [...this._allRows];

    const term = (this.pageFilters.search || '').toLowerCase().trim();
    if (term) {
      data = data.filter(
        (r: AccessGroupsVM) =>
          r.name.toLowerCase().includes(term) ||
          r.createdBy.some((x: string) => x.toLowerCase().includes(term)) ||
          r.role.toLowerCase().includes(term)
      );
    }

    if (this._currentSort) {
      data = this._sortingService.sortAccessGroups(data, this._currentSort);
    }

    this.accessGroups$.next(data);
    this.pageInfo.update((p) => ({ ...p, totalItems: data.length, currSize: data.length }));
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  public openCreateAccessGroupDialog(): void {
    const existingNames = this._allRows.map((row) => row.name);
    const dialogData: AccessGroupDialogData = { mode: 'create', existingNames };
    const ref = this._dialog.open(AccessGroupDialogComponent, {
      panelClass: 'access-group-dialog-panel',
      data: dialogData,
    });

    ref.closed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      const result = value as AccessGroupDialogResult | undefined;
      if (result) {
        this._createAccessGroup(result);
      }
    });
  }

  public onRowActionClicked(actionClicked: RowActionClicked): void {
    const action = actionClicked.action;
    const row = actionClicked.row.value as AccessGroupsVM;

    if (action.name === 'delete') {
      this._openDeleteConfirmationDialog(row);
      return;
    }
    if (action.name === 'edit') {
      this._openEditAccessGroupDialog(row);
      return;
    }
  }

  private _openEditAccessGroupDialog(row: AccessGroupsVM): void {
    const existingNames = this._allRows.map((r) => r.name);
    const dialogData: AccessGroupDialogData = {
      mode: 'edit',
      existingNames,
      accessGroup: {
        id: row.id,
        name: row.name,
        roleId: row.roleId,
        users: Array.isArray(row.users) ? row.users : [],
        integrationIds: row.integrationIds || [],
        allIntegrations: row.allIntegrations || false,
      },
    };

    const ref = this._dialog.open(AccessGroupDialogComponent, {
      panelClass: 'access-group-dialog-panel',
      data: dialogData,
    });

    ref.closed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      const result = value as AccessGroupDialogResult | undefined;
      if (result) {
        this._updateAccessGroup(row.id, result);
      }
    });
  }

  private _openDeleteConfirmationDialog(row: AccessGroupsVM): void {
    const confirmData: ConfirmData = {
      title: 'Delete access group?',
      content: `Are you sure you want to delete access group <strong>${row.name}</strong>?`,
      positiveOptionText: 'Delete',
      negativeOptionText: 'Cancel',
      icon: 'trash-2',
    };

    const dialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed === true),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._deleteAccessGroup(row.id, row.name);
      });
  }

  private _createAccessGroup(data: AccessGroupDialogResult): void {
    this.isLoading.set(true);

    this._accountHttpService
      .createAccessGroup({
        name: data.name,
        roleId: data.role || '',
        users: data.users || [],
        accessControls: data.allIntegrations
          ? []
          : (data.integrations || []).map((integrationId: string) => ({ integrationId })),
        allIntegrations: data.allIntegrations,
      })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (created) => {
          const newRow = this._transformToViewModel(created, data.allIntegrations);
          this._allRows = [newRow, ...this._allRows];
          this._applySortAndFilters();
          this._notificationsService.info(`${data.name} access group was created.`);
        },
        error: (error: HttpErrorResponse) => this._handleError(error),
      });
  }

  private _updateAccessGroup(id: string, data: AccessGroupDialogResult): void {
    this.isLoading.set(true);

    this._accountHttpService
      .updateAccessGroup(id, {
        name: data.name,
        roleId: data.role || '',
        users: data.users || [],
        accessControls: data.allIntegrations
          ? []
          : (data.integrations || []).map((integrationId: string) => ({ integrationId })),
        allIntegrations: data.allIntegrations,
      })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (updated) => {
          const updatedRow = this._transformToViewModel(updated, data.allIntegrations);
          this._allRows = this._allRows.map((r) => (r.id === id ? updatedRow : r));
          this._applySortAndFilters();
          this._notificationsService.info(`Changes to group ${data.name} saved.`);
        },
        error: (error: HttpErrorResponse) => this._handleError(error),
      });
  }

  private _deleteAccessGroup(id: string, name: string): void {
    this.isLoading.set(true);

    this._accountHttpService
      .deleteAccessGroup(id)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._allRows = this._allRows.filter((r) => r.id !== id);
          this._applySortAndFilters();
          this._notificationsService.info(`${name} access group was deleted.`);
        },
        error: (error: HttpErrorResponse) => this._handleError(error),
      });
  }

  private _transformToViewModel(
    apiResponse: AccessGroupApiResponse,
    allIntegrationsFallback?: boolean
  ): AccessGroupsVM {
    const accessControls = apiResponse.accessControls || [];
    const integrationIds = accessControls.map((ac) => ac.integrationId);
    // Empty accessControls array means "Everything" is enabled
    const allIntegrations =
      apiResponse.allIntegrations ?? (accessControls.length === 0 || allIntegrationsFallback) ?? false;
    const integrationNames = allIntegrations
      ? 'Everything'
      : accessControls
          .map((ac) => this._storeService.integrationsNamesById[ac.integrationId] || ac.integrationId)
          .join(', ');

    return {
      id: apiResponse.id,
      rowId: apiResponse.id,
      clickable: false,
      menuActions: this._rowActions,
      name: apiResponse.name,
      members: apiResponse.users?.length || 0,
      createdBy: apiResponse.updater ? [apiResponse.updater] : [],
      createdAt: new Date(apiResponse.createdTime),
      lastChanged: new Date(apiResponse.updatedTime),
      users: apiResponse.users || [],
      role: this._rolesMap[apiResponse.roleId] || getRoleDisplayName(apiResponse.roleId),
      roleId: apiResponse.roleId,
      integrations: integrationNames || '-',
      integrationIds: integrationIds,
      allIntegrations: allIntegrations,
    };
  }

  private _handleError(error: HttpErrorResponse): void {
    this._httpErrorHandlerService.handle(error);
    this._logger.error('Error in access groups:', error);
    this.showNoData.set(true);
  }

  private _applySearch(): void {
    this._applySortAndFilters();
  }
}
